
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, MessageSquare, Calendar, User, Plus, Users } from 'lucide-react';

const UserJourney = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  
  const steps = [
    {
      title: "Welcome to KidQuotes!",
      description: "We're excited to help you capture and cherish your child's memorable quotes and milestones.",
      icon: MessageSquare
    },
    {
      title: "Track Memorable Quotes",
      description: "Save those hilarious, insightful, or heartwarming things your kids say so you'll never forget them.",
      icon: MessageSquare
    },
    {
      title: "Create Your Family",
      description: "Start by creating a family - this is where all your children's memories will be organized. You'll be able to invite other family members later!",
      icon: Users
    },
    {
      title: "On This Day Memories",
      description: "Revisit quotes and milestones from the same calendar day in previous years.",
      icon: Calendar
    },
    {
      title: "Multiple Children",
      description: "Keep track of quotes from all your children in one place, organized by child.",
      icon: User
    },
    {
      title: "Ready to Start?",
      description: "Let's get you set up! First, we'll help you create your family, then you can add your children.",
      icon: Plus,
      isLast: true
    }
  ];

  const currentStep = steps[step];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onClose();
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleGetStarted = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{currentStep.title}</DialogTitle>
        </DialogHeader>

        <div className="py-6">
          <div className="flex items-center justify-center mb-6">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
              <currentStep.icon className="h-8 w-8 text-primary" />
            </div>
          </div>
          
          <p className="text-center text-muted-foreground mb-6">
            {currentStep.description}
          </p>

          <div className="flex justify-center gap-2 mb-4">
            {steps.map((_, index) => (
              <div 
                key={index}
                className={`h-2 w-2 rounded-full ${
                  index === step ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
          {!currentStep.isLast ? (
            <>
              <Button variant="ghost" onClick={handleSkip}>
                Skip Tour
              </Button>
              <Button onClick={handleNext} className="gap-2">
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleSkip}>
                Skip Setup
              </Button>
              <Button onClick={handleGetStarted} className="gap-2">
                Get Started <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserJourney;
